<template>
  <div>
    <session-list-add-new
      :is-add-new-session-sidebar-active.sync="isAddNewSessionSidebarActive"
      :visibility-options="visibilityOptions"
      :status-options="statusOptions"
      @refetch-data="refetchData"
    />

    <!-- Filters -->
    <session-list-filters
      :visibility-filter.sync="visibilityFilter"
      :status-filter.sync="statusFilter"
      :visibility-options="visibilityOptions"
      :status-options="statusOptions"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" placeholder="Search..." />
              <b-button variant="primary" @click="isAddNewSessionSidebarActive = true">
                <span class="text-nowrap">Add Session</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refSessionListTable"
        class="position-relative"
        :items="fetchSessions"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Session -->
        <template #cell(name)="data">
          <b-link
            :to="{ name: 'apps-roster-session-view', params: { id: data.item.id } }"
            class="font-weight-bold d-block text-nowrap"
          >
            {{ data.item.name }}
          </b-link>
          <small class="text-muted">{{ (data.item.courses || []).length }} total courses</small>
        </template>

        <!-- Column: Live Courses -->
        <template #cell(live_courses)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{
              data.item.courses.filter((course) => course.status === 'live').length
            }}</span>
          </div>
        </template>
        <!-- Column: Complete Courses -->
        <template #cell(completedCourses)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{
              data.item.courses.filter((courses) => courses.status === 'completed').length
            }}</span>
          </div>
        </template>
        <!-- Column: Total Students -->
        <template #cell(profiles)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.profiles.length }}</span>
          </div>
        </template>

        <!-- Column: Visibility -->
        <template #cell(visibility)="data">
          <b-badge pill :variant="`light-${resolveSessionVisibilityVariant(data.item.visibility)}`" class="text-capitalize">
            {{ data.item.visibility.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge pill :variant="`light-${resolveSessionStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ data.item.status.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item :to="{ name: 'apps-roster-session-view', params: { id: data.item.id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>

            <b-dropdown-item :to="{ name: 'apps-roster-session-edit', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="archiveSession(data.item.id)">
              <feather-icon icon="ArchiveIcon" />
              <span class="align-middle ml-50">Archive</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSessions"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText } from '@core/utils/filter';
import SessionListFilters from './SessionListFilters.vue';
import useSessionsList from './useSessionsList';
import rosterStoreModule from '../rosterStoreModule';
import SessionListAddNew from './SessionListAddNew.vue';

export default {
  components: {
    SessionListFilters,
    SessionListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  methods: {
    archiveSession(sessionId) {
      store
        .dispatch('app-roster/updateSession', {
          sessionId,
          sessionData: { status: 'archived' },
        })
        .then((response) => {
          this.refetchData();
        });
    },
  },
  setup() {
    const SESSION_APP_STORE_MODULE_NAME = 'app-roster';

    // Register module
    if (!store.hasModule(SESSION_APP_STORE_MODULE_NAME))
      store.registerModule(SESSION_APP_STORE_MODULE_NAME, rosterStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SESSION_APP_STORE_MODULE_NAME)) store.unregisterModule(SESSION_APP_STORE_MODULE_NAME);
    });

    const isAddNewSessionSidebarActive = ref(false);

    const {
      fetchSessions,
      tableColumns,
      perPage,
      currentPage,
      totalSessions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSessionListTable,
      refetchData,

      statusOptions,
      visibilityOptions,

      // UI
      resolveSessionVisibilityVariant,
      resolveSessionStatusVariant,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    } = useSessionsList();

    return {
      // Sidebar
      isAddNewSessionSidebarActive,

      fetchSessions,
      tableColumns,
      perPage,
      currentPage,
      totalSessions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSessionListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveSessionVisibilityVariant,
      resolveSessionStatusVariant,

      visibilityOptions,
      statusOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
